import React from 'react';
import { AlertCircle, Wifi, WifiOff, MicOff, RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ErrorType, getErrorMessage } from '../lib/errorHandler';
import { cn } from '../lib/utils';

interface ErrorDisplayProps {
  type: ErrorType;
  message?: string;
  onRetry?: (() => void) | undefined;
  onDismiss?: (() => void) | undefined;
  className?: string;
  variant?: 'card' | 'banner' | 'inline';
}

export function ErrorDisplay({ 
  type, 
  message, 
  onRetry, 
  onDismiss, 
  className,
  variant = 'card'
}: ErrorDisplayProps) {
  const errorInfo = getErrorMessage(type);
  const displayMessage = message || errorInfo.description;

  const getIcon = () => {
    switch (type) {
      case 'NETWORK_ERROR':
      case 'WEBSOCKET_ERROR':
        return WifiOff;
      case 'AUDIO_ERROR':
      case 'PERMISSION_ERROR':
      case 'FORMAT_ERROR':
        return MicOff;
      default:
        return AlertCircle;
    }
  };

  const getVariantStyles = () => {
    switch (type) {
      case 'NETWORK_ERROR':
      case 'WEBSOCKET_ERROR':
        return 'border-orange-200 bg-orange-50 text-orange-800';
      case 'AUDIO_ERROR':
      case 'PERMISSION_ERROR':
      case 'FORMAT_ERROR':
        return 'border-blue-200 bg-blue-50 text-blue-800';
      case 'VALIDATION_ERROR':
        return 'border-yellow-200 bg-yellow-50 text-yellow-800';
      default:
        return 'border-red-200 bg-red-50 text-red-800';
    }
  };

  const Icon = getIcon();

  if (variant === 'banner') {
    return (
      <div className={cn(
        'flex items-center gap-3 p-4 border rounded-lg',
        getVariantStyles(),
        className
      )}>
        <Icon className="h-5 w-5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium">{errorInfo.title}</div>
          <div className="text-sm opacity-90">{displayMessage}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {onRetry && (
            <Button
              onClick={onRetry}
              size="sm"
              variant="outline"
              className="bg-white/50 hover:bg-white/80"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          )}
          {onDismiss && (
            <Button
              onClick={onDismiss}
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2 text-sm', className)}>
        <Icon className="h-4 w-4 text-destructive" />
        <span className="text-destructive">{displayMessage}</span>
        {onRetry && (
          <Button onClick={onRetry} size="sm" variant="ghost" className="h-6 px-2">
            <RefreshCw className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  // Card variant (default)
  return (
    <Card className={cn('border-destructive/20', className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-destructive">
          <Icon className="h-5 w-5" />
          {errorInfo.title}
        </CardTitle>
        <CardDescription>{displayMessage}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {errorInfo.suggestions.length > 0 && (
          <div>
            <h4 className="font-medium text-sm mb-2">Try these solutions:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              {errorInfo.suggestions.map((suggestion, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-muted-foreground">•</span>
                  <span>{suggestion}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {(onRetry || onDismiss) && (
          <div className="flex gap-2">
            {onRetry && (
              <Button onClick={onRetry} size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            )}
            {onDismiss && (
              <Button onClick={onDismiss} size="sm" variant="outline">
                Dismiss
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Specialized error components for common scenarios
interface ConnectionErrorProps {
  isConnecting?: boolean;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export function ConnectionError({ isConnecting, onRetry, onDismiss, className }: ConnectionErrorProps) {
  return (
    <ErrorDisplay
      type="WEBSOCKET_ERROR"
      message={isConnecting ? "Attempting to reconnect..." : "Connection lost. Click retry to reconnect."}
      onRetry={onRetry}
      onDismiss={onDismiss}
      className={className || ""}
      variant="banner"
    />
  );
}

interface AudioErrorProps {
  errorType: 'permission' | 'format' | 'device';
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export function AudioError({ errorType, onRetry, onDismiss, className }: AudioErrorProps) {
  const getErrorType = (): ErrorType => {
    switch (errorType) {
      case 'permission':
        return 'PERMISSION_ERROR';
      case 'format':
        return 'FORMAT_ERROR';
      case 'device':
      default:
        return 'AUDIO_ERROR';
    }
  };

  return (
    <ErrorDisplay
      type={getErrorType()}
      onRetry={onRetry}
      onDismiss={onDismiss}
      className={className || ""}
    />
  );
}

// Status indicator with error state
interface StatusIndicatorProps {
  status: 'connected' | 'connecting' | 'disconnected' | 'reconnecting' | 'error';
  error?: string | undefined;
  className?: string;
}

export function StatusIndicator({ status, error, className }: StatusIndicatorProps) {
  const getStatusConfig = () => {
    switch (status) {
      case 'connected':
        return {
          icon: Wifi,
          color: 'text-green-500',
          bgColor: 'bg-green-100',
          text: 'Connected',
          variant: 'default' as const
        };
      case 'connecting':
        return {
          icon: RefreshCw,
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-100',
          text: 'Connecting...',
          variant: 'secondary' as const,
          animate: true
        };
      case 'reconnecting':
        return {
          icon: RefreshCw,
          color: 'text-orange-500',
          bgColor: 'bg-orange-100',
          text: 'Reconnecting...',
          variant: 'secondary' as const,
          animate: true
        };
      case 'disconnected':
        return {
          icon: WifiOff,
          color: 'text-gray-500',
          bgColor: 'bg-gray-100',
          text: 'Disconnected',
          variant: 'secondary' as const
        };
      case 'error':
        return {
          icon: AlertCircle,
          color: 'text-red-500',
          bgColor: 'bg-red-100',
          text: error || 'Error',
          variant: 'destructive' as const
        };
      default:
        // Fallback for unknown status
        return {
          icon: AlertCircle,
          color: 'text-gray-500',
          bgColor: 'bg-gray-100',
          text: 'Unknown',
          variant: 'secondary' as const
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className={cn('p-1 rounded-full', config.bgColor)}>
        <Icon className={cn('h-3 w-3', config.color, {
          'animate-spin': config.animate
        })} />
      </div>
      <Badge variant={config.variant} className="text-xs">
        {config.text}
      </Badge>
    </div>
  );
}

// Error toast notification
interface ErrorToastProps {
  type: ErrorType;
  message?: string;
  onDismiss: () => void;
  autoHide?: boolean;
  duration?: number;
}

export function ErrorToast({ 
  type, 
  message, 
  onDismiss, 
  autoHide = true, 
  duration = 5000 
}: ErrorToastProps) {
  React.useEffect(() => {
    if (autoHide) {
      const timer = setTimeout(onDismiss, duration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoHide, duration, onDismiss]);

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <ErrorDisplay
        type={type}
        message={message || ""}
        onDismiss={onDismiss}
        variant="banner"
        className="shadow-lg"
      />
    </div>
  );
}